import cx from 'classnames'
import NextImage from 'next/image'
import { CSSProperties, useState } from 'react'
import { SanityImageFragment } from '@data/sanity/queries/types/image'
import {
  ImageDimensions,
  getImageDimensions,
  getSanityImageLoader,
} from '@lib/image'
import Link from '@components/link'
import { borderRadiusClassMap } from '@lib/border'

interface PhotoProps {
  image: SanityImageFragment
  width?: number
  height?: number
  sizes?: string
  fill?: boolean
  priority?: boolean
  unoptimized?: boolean
  quality?: number
  className?: string
  imageClassName?: string
  style?: CSSProperties
  imageStyle?: CSSProperties
  showCaption?: boolean
  showPlaceholder?: boolean
  fadeIn?: boolean
}

const Photo = ({
  image,
  width,
  height,
  sizes,
  fill,
  priority,
  unoptimized,
  quality = 75,
  className,
  imageClassName,
  style,
  imageStyle,
  showCaption = true,
  showPlaceholder = true,
  fadeIn,
}: PhotoProps) => {
  const [isLoaded, setIsLoaded] = useState(false)

  if (!image || !image.asset) {
    return null
  }

  const placeholder = showPlaceholder
    ? (image.asset.metadata?.lqip as `data:image/${string}` | undefined)
    : undefined
  const dimensions: ImageDimensions | undefined = !fill
    ? getImageDimensions(image, width, height)
    : undefined
  const src = JSON.stringify({
    ...image,
    // Insert custom ratio from props into image data to force image aspect ratio
    customRatio:
      width && height
        ? width / height
        : 'customRatio' in image
          ? image.customRatio
          : undefined,
  })

  const figure = (
    <figure className={cx('photo', className)} style={style}>
      <NextImage
        src={src}
        loader={getSanityImageLoader}
        width={dimensions?.width}
        height={dimensions?.height}
        sizes={sizes}
        fill={fill}
        unoptimized={unoptimized}
        priority={priority}
        quality={quality}
        placeholder={placeholder}
        alt={image.alt ?? ''}
        onLoad={() => setIsLoaded(true)}
        className={cx(
          'block overflow-hidden transition-opacity duration-500',
          'borderRadius' in image && image.borderRadius
            ? borderRadiusClassMap[image.borderRadius ?? '']
            : '',
          {
            'opacity-0': fadeIn && !isLoaded,
          },
          imageClassName,
        )}
        style={imageStyle}
      />

      {showCaption && 'caption' in image && !!image.caption && (
        <figcaption className="text-current text-sm my-2">
          {image.caption}
        </figcaption>
      )}
    </figure>
  )

  if ('link' in image && image.link) {
    return (
      <Link link={image.link} className="no-underline">
        {figure}
      </Link>
    )
  }

  return figure
}

export default Photo
